import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';

const LABEL_MAP = {
  id: 'ID', name: 'Nombre', sponsor: 'Sponsor', status: 'Estado',
  priority: 'Prioridad', programId: 'Gerencia', initiativeId: 'Iniciativa',
  projectId: 'Proyecto', targetId: 'Objetivo', targetName: 'Nombre objetivo',
  requestType: 'Tipo solicitud', requestedBy: 'Solicitado por', comments: 'Comentarios',
  title: 'Título', probability: 'Probabilidad', impact: 'Impacto', owner: 'Responsable',
  origin: 'Origen', value: 'Valor esperado', urgency: 'Urgencia', objective: 'Objetivo',
  type: 'Tipo', expected: 'Beneficio esperado', realized: 'Realizado',
  severity: 'Severidad', description: 'Descripción', entityType: 'Tipo entidad',
  action: 'Acción', user: 'Usuario', entityId: 'ID entidad', changes: 'Cambios',
  statusHistory: 'Historial de estados', approver: 'Aprobador',
  dependsOnProjectId: 'Depende de',
  context: 'Contexto', metric: 'Métrica', target: 'Objetivo', thresholdYellow: 'Umbral amarillo', thresholdRed: 'Umbral rojo', periodicity: 'Periodicidad',
  currentValue: 'Valor actual', complianceStatus: 'Cumplimiento', sampleSize: 'Muestras del periodo', resolvedSampleSize: 'Resueltas del periodo', periodStart: 'Inicio del periodo', calculatedAt: 'Último cálculo',
  timestamp: 'Fecha', createdAt: 'Creado', updatedAt: 'Actualizado',
};

const CHIP_STATUS = {
  Completado: 'success', 'En ejecución': 'primary', Propuesto: 'default',
  Aprobado: 'success', Rechazado: 'error', Pendiente: 'warning',
  Abierto: 'warning', Cerrado: 'success', 'En revisión': 'primary', 'En progreso': 'primary',
  Solicitado: 'warning', Implementado: 'success',
  Activo: 'success', Suspendido: 'default', Activa: 'warning', Resuelta: 'success',
  Cumple: 'success', 'En riesgo': 'warning', Incumplido: 'error', 'Sin datos': 'default',
  Alta: 'error', Media: 'warning', Baja: 'default',
  Planificado: 'default', Realizado: 'success',
};

const SKIP_KEYS = ['id'];
const RO_KEYS = ['createdAt', 'updatedAt', 'timestamp', 'entityType', 'action', 'user', 'entityId', 'changes'];

const TYPE_LABELS = {
  project: 'Proyecto', program: 'Gerencia', initiative: 'Iniciativa',
  risk: 'Riesgo', document: 'Documento', change: 'Solicitud de cambio', finance: 'Presupuesto', approval: 'Aprobación',
  dependency: 'Dependencia', sla: 'SLA', benefit: 'Beneficio', audit: 'Auditoría', incident: 'Incidente', auditLog: 'Audit Log',
};

const SELECT_OPTS = {
  status: ['Propuesto','En ejecución','Completado','Pendiente','Aprobado','Rechazado','Abierto','En revisión','Cerrado','En progreso','Planificado','Realizado'],
  priority: ['Baja','Media','Alta'], urgency: ['Baja','Media','Alta'],
  probability: ['Baja','Media','Alta'], impact: ['Baja','Media','Alta'],
  severity: ['Baja','Media','Alta'], requestType: ['Proyecto','Gerencia','Iniciativa'],
};

export default function DetailsPanel({ type, item, onClose, onSave, projects = [], programs = [], initiatives = [], showToast }) {
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState(item || {});
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(item || {}); setEditMode(false); }, [item]);

  if (!item) return null;
  const keys = Object.keys(form).filter((k) => !SKIP_KEYS.includes(k));

  const formatDisplay = (key, value) => {
    if (value == null || value === '') return <Typography variant="body2" color="text.disabled">—</Typography>;
    if (key === 'projectId') { const p = projects.find(x => x.id === value); return <Typography variant="body2">{p ? p.name : value}</Typography>; }
    if (key === 'dependsOnProjectId') { const p = projects.find(x => x.id === value); return <Typography variant="body2">{p ? p.name : value}</Typography>; }
    if (key === 'programId') { const p = programs.find(x => x.id === value); return <Typography variant="body2">{p ? p.name : value}</Typography>; }
    if (key === 'initiativeId') { const p = initiatives.find(x => x.id === value); return <Typography variant="body2">{p ? p.name : value}</Typography>; }
    if (key === 'targetId') { const p = [...projects,...programs,...initiatives].find(x => x.id === value); return <Typography variant="body2">{p ? p.name : value}</Typography>; }
    if (key === 'changes') return <Typography variant="body2" sx={{ fontFamily:'monospace', fontSize:12, wordBreak:'break-all' }}>{typeof value==='object'?JSON.stringify(value,null,2):String(value)}</Typography>;
    if (RO_KEYS.includes(key) && (key==='createdAt'||key==='updatedAt'||key==='timestamp')) {
      try { return <Typography variant="body2">{new Date(value).toLocaleString()}</Typography>; } catch { return <Typography variant="body2">{String(value)}</Typography>; }
    }
    if (CHIP_STATUS[value] !== undefined) return <Chip label={value} size="small" color={CHIP_STATUS[value]} />;
    return <Typography variant="body2">{String(value)}</Typography>;
  };

  const renderEdit = (key) => {
    if (RO_KEYS.includes(key)) return formatDisplay(key, form[key]);
    const lbl = LABEL_MAP[key] || key;
    if (key === 'projectId') return <FormControl size="small" fullWidth><InputLabel>Proyecto</InputLabel><Select label="Proyecto" value={form[key]||''} onChange={e=>setForm({...form,[key]:e.target.value})}><MenuItem value="">—</MenuItem>{projects.map(p=><MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}</Select></FormControl>;
    if (key === 'programId') return <FormControl size="small" fullWidth><InputLabel>Gerencia</InputLabel><Select label="Gerencia" value={form[key]||''} onChange={e=>setForm({...form,[key]:e.target.value})}><MenuItem value="">—</MenuItem>{programs.map(p=><MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}</Select></FormControl>;
    if (key === 'initiativeId') return <FormControl size="small" fullWidth><InputLabel>Iniciativa</InputLabel><Select label="Iniciativa" value={form[key]||''} onChange={e=>setForm({...form,[key]:e.target.value})}><MenuItem value="">—</MenuItem>{initiatives.map(p=><MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}</Select></FormControl>;
    if (SELECT_OPTS[key]) return <FormControl size="small" fullWidth><InputLabel>{lbl}</InputLabel><Select label={lbl} value={form[key]||''} onChange={e=>setForm({...form,[key]:e.target.value})}>{SELECT_OPTS[key].map(o=><MenuItem key={o} value={o}>{o}</MenuItem>)}</Select></FormControl>;
    return <TextField size="small" fullWidth value={form[key]??''} onChange={e=>setForm({...form,[key]:e.target.value})} multiline={['description','comments'].includes(key)} rows={['description','comments'].includes(key)?3:1} />;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const ok = await onSave(type, form.id || item.id, form);
      if (ok === false) showToast?.('Error al guardar los cambios', 'error');
      else { showToast?.('Guardado correctamente', 'success'); setEditMode(false); }
    } catch { showToast?.('Error al guardar', 'error'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={Boolean(item)} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', pb:1 }}>
        <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
          <Typography variant="h6">{TYPE_LABELS[type] || type}</Typography>
          {(item.name || item.title) && <Typography variant="body2" color="text.secondary">— {item.name || item.title}</Typography>}
        </Box>
        <Box sx={{ display:'flex', gap:0.5 }}>
          {!editMode && type !== 'auditLog' && <IconButton size="small" onClick={()=>setEditMode(true)}><EditIcon fontSize="small" /></IconButton>}
          <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt:2 }}>
        <Box sx={{ display:'grid', gap:2 }}>
          {keys.map((key) => (
            <Box key={key}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em' }}>
                {LABEL_MAP[key] || key}
              </Typography>
              <Box sx={{ mt:0.5 }}>{editMode ? renderEdit(key) : formatDisplay(key, form[key])}</Box>
            </Box>
          ))}
        </Box>
      </DialogContent>
      {editMode && (
        <>
          <Divider />
          <DialogActions>
            <Button onClick={()=>{setEditMode(false);setForm(item||{});}} disabled={saving}>Cancelar</Button>
            <Button variant="contained" onClick={handleSave} disabled={saving} startIcon={saving?<CircularProgress size={16}/>:null}>
              {saving ? 'Guardando…' : 'Guardar cambios'}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
